// components/AddToCartButton.jsx - UPDATED VERSION
import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import useUserStore from '../store/userStore';

const AddToCartButton = ({
  isInCart: externalIsInCart,
  slug,
  price,
  collection,
  media_type,
  onAddToCart,
  className,
}) => {
  // Предотвращаем начальный рендер до получения правильных данных
  const [isReady, setIsReady] = useState(false);
  const [localIsInCart, setLocalIsInCart] = useState(externalIsInCart);
  const [isLoading, setIsLoading] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [error, setError] = useState(false);
  const [animateClick, setAnimateClick] = useState(false);
  
  // Access user store for auth state
  const { isLoggedIn } = useUserStore();
  
  // Отслеживаем, когда происходит действие пользователя
  const isUserAction = useRef(false);
  
  // Первоначальная установка состояния и предотвращение "прыжка"
  useEffect(() => {
    // При монтировании компонента, устанавливаем начальное состояние
    setLocalIsInCart(externalIsInCart);
    
    // Отложенно разрешаем рендер, чтобы все стили применились корректно
    const timer = setTimeout(() => {
      setIsReady(true);
    }, 50);
    
    return () => clearTimeout(timer);
  }, []);
  
  // Обновляем состояние при внешних изменениях (без анимации)
  useEffect(() => {
    if (isReady && !isUserAction.current) {
      setLocalIsInCart(externalIsInCart);
    }
  }, [externalIsInCart, isReady]);
  
  // Обработка анимации при клике
  useEffect(() => {
    if (animateClick) {
      const timer = setTimeout(() => {
        setAnimateClick(false);
        isUserAction.current = false;
      }, 600);
      
      return () => clearTimeout(timer);
    }
  }, [animateClick]);
  
  // Сбрасываем ошибку через таймаут
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [error]);
  
  const handleAddToCart = async () => {
    if (isLoading) return;
    
    setIsLoading(true);
    setError(false);
    
    // Устанавливаем флаг пользовательского действия и анимации
    isUserAction.current = true;
    setAnimateClick(true);
    
    try {
      // Обновляем UI с анимацией
      setLocalIsInCart(true);
      
      const animationPromise = new Promise(resolve => {
        setTimeout(resolve, 300);
      });
      
      const [, apiResult] = await Promise.allSettled([
        animationPromise,
        onAddToCart ? onAddToCart() : Promise.resolve(true)
      ]);
      
      if (apiResult.status === 'rejected') {
        console.error('Background error in onAddToCart:', apiResult.reason);
      }
    } catch (error) {
      console.error('Error in handleAddToCart:', error);
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 300);
    }
  };

  // ОБНОВЛЕННАЯ ФУНКЦИЯ CHECKOUT
  const handleCheckout = async () => {
    if (checkoutLoading) return;
    
    setCheckoutLoading(true);
    try {
      // Проверяем, авторизован ли пользователь
      if (!isLoggedIn) {
        // Если не авторизован, перенаправляем на страницу логина
        const currentUrl = window.location.href;
        window.location.href = `/login?next=${encodeURIComponent(currentUrl)}`;
        return;
      }

      // НОВОЕ: Принудительно обновляем токен перед чекаутом
      const freshToken = await useUserStore.getState().refreshTokenIfNeeded(true);
      if (!freshToken) {
        console.error('Failed to refresh token for checkout');
        const currentUrl = window.location.href;
        window.location.href = `/login?next=${encodeURIComponent(currentUrl)}`;
        return;
      }

      // НОВОЕ: Используем fetchWithTokenRefresh вместо обычного fetch
      const checkoutResponse = await useUserStore.getState().fetchWithTokenRefresh(
        `${process.env.NEXT_PUBLIC_BACKEND_URL}/cart/checkout`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ return_url: window.location.href }),
        }
      );
      
      if (!checkoutResponse.ok) {
        let errorMessage = 'Checkout failed';
        try {
          const errorData = await checkoutResponse.json();
          errorMessage = errorData.detail || errorMessage;
        } catch (e) {
          // Если не удалось распарсить JSON
          errorMessage = await checkoutResponse.text();
        }
        
        alert(`Error: ${errorMessage}`);
        setCheckoutLoading(false);
        return;
      }

      // Получаем URL для чекаута и перенаправляем
      const data = await checkoutResponse.json();
      window.location.href = data.checkout_url;
      
    } catch (error) {
      console.error('Checkout error:', error);
      alert('An error occurred during checkout. Please try again.');
      setCheckoutLoading(false);
    }
  };

  // Не показываем ничего до завершения начальной настройки
  if (!isReady) {
    return (
      <div className={`relative min-h-[60px] overflow-hidden ${className || ''}`} 
           style={{ visibility: 'hidden' }}></div>
    );
  }

  return (
    <div className={`relative min-h-[60px] overflow-hidden ${className || ''}`}>
      {/* Кнопка "Добавить в корзину" */}
      <button
        className={`
          absolute w-full h-full text-base cursor-pointer border-none rounded-md text-white z-10 font-bold pl-5 bg-black
          ${animateClick ? 'transition-transform duration-500 ease-in-out' : ''}
          ${localIsInCart ? 'translate-x-full opacity-0' : 'translate-x-0 opacity-100'}
          ${isLoading ? 'opacity-70 cursor-wait' : ''}
        `}
        onClick={handleAddToCart}
        disabled={isLoading}
      >
        {`Add SVG to Cart $${price.toFixed(2)}`}
        
        {isLoading && (
          <span className="ml-4 inline-block align-middle">
            <svg 
              width="24" 
              height="24" 
              viewBox="0 0 100 100" 
              preserveAspectRatio="xMidYMid" 
              className="align-middle"
            >
              <circle cx="50" cy="50" fill="none" stroke="#fff" strokeWidth="8" r="35" strokeDasharray="164.93361431346415 56.97787143782138">
                <animateTransform 
                  attributeName="transform" 
                  type="rotate" 
                  repeatCount="indefinite" 
                  dur="1s" 
                  values="0 50 50;360 50 50" 
                  keyTimes="0;1"
                />
              </circle>
            </svg>
          </span>
        )}
      </button>

      {/* Кнопка "Перейти к оформлению" */}
      <button
        className={`
          absolute w-full h-full text-base cursor-pointer border-none rounded-md text-white z-[1] font-bold pl-5 bg-black
          ${animateClick ? 'transition-transform duration-500 ease-in-out' : ''}
          ${localIsInCart ? 'translate-x-0 opacity-100' : '-translate-x-full opacity-0'}
          ${checkoutLoading ? 'opacity-70 cursor-wait' : ''}
        `}
        onClick={handleCheckout}
        disabled={checkoutLoading}
      >
        {"Proceed to Checkout"}
        {checkoutLoading && (
          <span className="ml-4 inline-block align-middle">
            <svg 
              width="24" 
              height="24" 
              viewBox="0 0 100 100" 
              preserveAspectRatio="xMidYMid" 
              className="align-middle"
            >
              <circle cx="50" cy="50" fill="none" stroke="#fff" strokeWidth="8" r="35" strokeDasharray="164.93361431346415 56.97787143782138">
                <animateTransform 
                  attributeName="transform" 
                  type="rotate" 
                  repeatCount="indefinite" 
                  dur="1s" 
                  values="0 50 50;360 50 50" 
                  keyTimes="0;1"
                />
              </circle>
            </svg>
          </span>
        )}
      </button>
    </div>
  );
};

AddToCartButton.propTypes = {
  isInCart: PropTypes.bool.isRequired,
  slug: PropTypes.string.isRequired,
  price: PropTypes.number.isRequired,
  collection: PropTypes.string.isRequired,
  media_type: PropTypes.string.isRequired,
  onAddToCart: PropTypes.func,
  className: PropTypes.string,
};

export default AddToCartButton;